import React, { useEffect, useMemo, useState } from "react";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { STAGES } from "@/lib/config";
import { useTheme } from "@/lib/theme";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ExternalLink,
  Copy,
  Link as LinkIcon,
  FileText,
  Save,
  Plus,
  MapPin,
  Building2,
  Calendar,
  Mail,
  ArrowRight,
  Receipt,
  ChevronDown,
  Check,
  Loader2,
  Download,
} from "lucide-react";

const DELIVERABLE_STAGES = [
  "Order Received",
  "Assets Downloaded",
  "Editor Assigned",
  "Editing",
  "Quality Check",
  "Delivered",
];

const ADMIN_STATUSES = [
  "Pending",
  "Awaiting Content",
  "In Production",
  "Revision Requested",
  "Completed",
  "Delivered",
  "Cancelled",
];

/* ------- Relative-time formatter for the admin orders table ------- */
function formatRelative(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diff = Math.max(0, Date.now() - then);
  const sec = Math.floor(diff / 1000);
  if (sec < 45) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.floor(hr / 24);
  if (day < 3) return `${day} day${day === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year:
      new Date(iso).getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  });
}

// Renders a compact package summary that works for both legacy and multi-service orders
function packageSummary(o) {
  if (Array.isArray(o.services) && o.services.length) {
    const total = o.services.reduce((s, x) => s + (x.quantity || 0), 0);
    return o.services.length > 1
      ? `${total} pieces · ${o.services.length} services`
      : `${total} · ${o.services[0].style_id}`;
  }
  return `${o.package_clips || 0} · ${o.editing_style || "—"}`;
}

export default function Admin() {
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [editors, setEditors] = useState([]);

  const load = async () => {
    const [oR, cR, aR, eR] = await Promise.all([
      api.get("/admin/orders"),
      api.get("/admin/customers"),
      api.get("/admin/analytics"),
      api.get("/editors"),
    ]);
    setOrders(oR.data);
    setCustomers(cR.data);
    setAnalytics(aR.data);
    setEditors(eR.data);
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <Nav />
      <div className="pt-28 pb-24 px-6 sm:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-xs uppercase tracking-widest text-[#a0a0a0] mb-2">
            Admin console
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter" data-testid="admin-title">
            Studio operations
          </h1>

          <Tabs value={tab} onValueChange={setTab} className="mt-10">
            <TabsList
              data-testid="admin-tabs"
              className="bg-[#0d0d0d] border border-[#2b2b2b] p-1 squircle-sm"
            >
              <TabsTrigger data-testid="tab-orders" value="orders" className="data-[state=active]:bg-white data-[state=active]:text-black squircle-sm px-4 py-2 text-sm">
                Orders
              </TabsTrigger>
              <TabsTrigger data-testid="tab-customers" value="customers" className="data-[state=active]:bg-white data-[state=active]:text-black squircle-sm px-4 py-2 text-sm">
                Customers
              </TabsTrigger>
              <TabsTrigger data-testid="tab-analytics" value="analytics" className="data-[state=active]:bg-white data-[state=active]:text-black squircle-sm px-4 py-2 text-sm">
                Analytics
              </TabsTrigger>
              <TabsTrigger data-testid="tab-prime" value="prime" className="data-[state=active]:bg-white data-[state=active]:text-black squircle-sm px-4 py-2 text-sm">
                Prime
              </TabsTrigger>
              <TabsTrigger data-testid="tab-affiliates" value="affiliates" className="data-[state=active]:bg-white data-[state=active]:text-black squircle-sm px-4 py-2 text-sm">
                Affiliates
              </TabsTrigger>
            </TabsList>

            <TabsContent value="orders" className="mt-8">
              <OrdersPanel orders={orders} editors={editors} reload={load} />
            </TabsContent>
            <TabsContent value="customers" className="mt-8">
              <CustomersPanel customers={customers} editors={editors} reload={load} />
            </TabsContent>
            <TabsContent value="analytics" className="mt-8">
              <AnalyticsPanel data={analytics} />
            </TabsContent>
            <TabsContent value="prime" className="mt-8">
              <PrimePanel />
            </TabsContent>
            <TabsContent value="affiliates" className="mt-8">
              <AffiliatesPanel />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

function OrdersPanel({ orders, editors, reload }) {
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("");

  const filtered = orders.filter((o) => {
    if (stageFilter && o.stage !== stageFilter) return false;
    if (q) {
      const s = q.toLowerCase();
      return (
        (o.order_id || "").toLowerCase().includes(s) ||
        (o.user_email || "").toLowerCase().includes(s) ||
        (o.user_name || "").toLowerCase().includes(s) ||
        (o.editing_style || "").toLowerCase().includes(s) ||
        (o.primary_service_name || "").toLowerCase().includes(s)
      );
    }
    return true;
  });

  const advance = async (id, currentStage) => {
    const i = STAGES.indexOf(currentStage);
    if (i >= STAGES.length - 1) return;
    try {
      await api.patch(`/orders/${id}`, { stage: STAGES[i + 1] });
      toast.success(`Moved to "${STAGES[i + 1]}"`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const assign = async (id, editor_id) => {
    try {
      await api.patch(`/orders/${id}`, { editor_id });
      toast.success("Editor assigned");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign editor");
    }
  };

  const setStageDirect = async (id, stage) => {
    try {
      await api.patch(`/orders/${id}`, { stage });
      toast.success(`Stage set: ${stage}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update stage");
    }
  };

  const uploadDeliverable = async (id) => {
    const url = window.prompt("Paste deliverable URL");
    if (!url) return;
    const name = window.prompt("Label (optional)") || "File";
    const target = orders.find((o) => o.order_id === id);
    const next = [...(target.deliverables || []), { url, name }];
    try {
      await api.patch(`/orders/${id}`, { deliverables: next });
      toast.success("Deliverable added");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add deliverable");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <input
          data-testid="admin-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search orders, customers…"
          className="flex-1 min-w-[240px] squircle-sm bg-transparent border border-[#2b2b2b] px-4 py-2.5 text-sm focus:border-white outline-none"
        />
        <select
          data-testid="admin-stage-filter"
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          className="squircle-sm bg-[#0d0d0d] border border-[#2b2b2b] px-4 py-2.5 text-sm"
        >
          <option value="">All stages</option>
          {STAGES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div className="squircle border border-[#2b2b2b] overflow-hidden">
        <div className="grid grid-cols-12 bg-[#0d0d0d] text-xs uppercase tracking-widest text-[#a0a0a0] px-4 py-3">
          <div className="col-span-3">Customer</div>
          <div className="col-span-2">Package · Submitted</div>
          <div className="col-span-2">Stage</div>
          <div className="col-span-2">Editor</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        {filtered.length === 0 && (
          <div className="p-8 text-[#a0a0a0] text-sm text-center">No orders match.</div>
        )}
        {filtered.map((o, i) => (
          <motion.div
            key={o.order_id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: i * 0.02 }}
            className={`grid grid-cols-12 px-4 py-4 border-t border-[#1e1e1e] items-center ${
              o.stage === "Delivered" || o.admin_status === "Delivered"
                ? ""
                : "bg-[#111]"
            }`}
            data-testid={`admin-order-${o.order_id}`}
            data-delivered={o.stage === "Delivered" || o.admin_status === "Delivered" ? "1" : "0"}
            style={
              o.stage === "Delivered" || o.admin_status === "Delivered"
                ? {
                    background:
                      "linear-gradient(180deg, rgba(34,197,94,0.08) 0%, rgba(34,197,94,0.02) 100%)",
                    boxShadow: "inset 3px 0 0 #22c55e",
                  }
                : undefined
            }
          >
            <div className="col-span-3">
              <div className="font-medium flex items-center gap-2">
                {o.user_name || o.user_email}
                {(o.stage === "Delivered" || o.admin_status === "Delivered") && (
                  <span
                    className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                    style={{
                      background: "rgba(34,197,94,0.15)",
                      color: "#22c55e",
                      border: "1px solid rgba(34,197,94,0.4)",
                    }}
                  >
                    Delivered
                  </span>
                )}
              </div>
              <div className="text-xs text-[#a0a0a0] truncate">
                #{o.order_id.slice(-6)} · {o.user_email}
              </div>
            </div>
            <div className="col-span-2 text-sm">
              <div>{packageSummary(o)}</div>
              <div
                className="text-xs text-[#a0a0a0] mt-1"
                title={new Date(o.created_at).toLocaleString()}
                data-testid={`admin-submitted-${o.order_id}`}
              >
                {formatRelative(o.created_at)}
              </div>
            </div>
            <div className="col-span-2 pr-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-widest text-[#7a7a7a]">
                  Stage
                </span>
                <span className="text-[10px] text-[#7a7a7a]">
                  {Math.max(0, STAGES.indexOf(o.stage)) + 1}/{STAGES.length}
                </span>
              </div>
              <select
                data-testid={`table-stage-picker-${o.order_id}`}
                value={o.stage}
                onChange={(e) => setStageDirect(o.order_id, e.target.value)}
                className="w-full squircle-sm bg-[#0d0d0d] border border-[#2b2b2b] px-2.5 h-8 text-xs"
              >
                {STAGES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2 pl-1">
              <div className="text-[10px] uppercase tracking-widest text-[#7a7a7a] mb-1">
                Editor
              </div>
              <select
                data-testid={`assign-editor-${o.order_id}`}
                value={o.editor_id || ""}
                onChange={(e) => assign(o.order_id, e.target.value || null)}
                className="w-full squircle-sm bg-[#0d0d0d] border border-[#2b2b2b] px-2.5 h-8 text-xs"
              >
                <option value="">Unassigned</option>
                {editors.map((e) => (
                  <option key={e.editor_id} value={e.editor_id}>
                    {e.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-3 flex justify-end gap-2 flex-wrap">
              <ReceiptButton orderId={o.order_id} />
              <button
                data-testid={`advance-${o.order_id}`}
                onClick={() => advance(o.order_id, o.stage)}
                disabled={o.stage === "Delivered"}
                className="squircle-sm border border-[#2b2b2b] px-3 py-1.5 text-xs hover:bg-[#0d0d0d] btn-anim disabled:opacity-40"
              >
                Advance
              </button>
              <button
                data-testid={`upload-${o.order_id}`}
                onClick={() => uploadDeliverable(o.order_id)}
                className="squircle-sm bg-white text-black px-3 py-1.5 text-xs font-medium hover:bg-[#f2f2f2] btn-anim"
              >
                Add deliverable
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function CustomersPanel({ customers, editors, reload }) {
  const [activeId, setActiveId] = useState(null);
  const active = customers.find((c) => c.user_id === activeId) || null;

  return (
    <>
      <div className="squircle border border-[#2b2b2b] overflow-hidden" data-testid="admin-customers">
        <div className="grid grid-cols-12 bg-[#0d0d0d] text-xs uppercase tracking-widest text-[#a0a0a0] px-4 py-3">
          <div className="col-span-4">Name</div>
          <div className="col-span-4">Email</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2 text-right">Orders</div>
        </div>
        {customers.map((c) => (
          <button
            key={c.user_id}
            onClick={() => setActiveId(c.user_id)}
            data-testid={`customer-row-${c.user_id}`}
            className="w-full text-left grid grid-cols-12 px-4 py-4 border-t border-[#1e1e1e] items-center bg-[#111] hover:bg-[#141414] btn-anim"
          >
            <div className="col-span-4 flex items-center gap-2">
              <span className="text-white">{c.name}</span>
              <ArrowRight size={12} className="text-[#555] opacity-0 group-hover:opacity-100" />
            </div>
            <div className="col-span-4 text-sm text-[#a0a0a0] truncate">{c.email}</div>
            <div className="col-span-2 text-sm capitalize text-[#a0a0a0]">{c.role}</div>
            <div className="col-span-2 text-right font-semibold">{c.order_count}</div>
          </button>
        ))}
      </div>

      <AnimatePresence>
        {active && (
          <CustomerDetailPanel
            key={active.user_id}
            userId={active.user_id}
            editors={editors}
            onClose={() => setActiveId(null)}
            onChanged={reload}
          />
        )}
      </AnimatePresence>
    </>
  );
}

/* =================================================================== */
/* CustomerDetailPanel — side-in workspace                             */
/* =================================================================== */
function CustomerDetailPanel({ userId, editors, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const { theme } = useTheme();
  const isLight = theme === "light";

  const load = async () => {
    // Only show the full-screen spinner on the very first load. Subsequent
    // refetches (e.g. after a deliverable save) happen silently so that
    // per-card UI state — open editors, unsaved edits — isn't destroyed.
    const isFirst = !detail;
    if (isFirst) setLoading(true);
    try {
      const { data } = await api.get(`/admin/customers/${userId}`);
      setDetail(data);
    } finally {
      if (isFirst) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Theme-adaptive tokens (applied via inline style so we don't fight the
  // global `html.light` overrides that flip hardcoded utility classes).
  const t = isLight
    ? {
        bg: "#ffffff",
        border: "#e5e5ea",
        surface: "#f5f5f7",
        surface2: "#fafafc",
        text: "#0a0a0a",
        muted: "#6a6a70",
        input: "#ffffff",
      }
    : {
        bg: "#0a0a0a",
        border: "#2b2b2b",
        surface: "#0d0d0d",
        surface2: "#111",
        text: "#ffffff",
        muted: "#a0a0a0",
        input: "#0d0d0d",
      };

  return (
    <motion.div
      className="fixed inset-0 z-[200] flex justify-end"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      data-testid="customer-detail-panel"
      data-theme={theme}
    >
      {/* Backdrop */}
      <motion.div
        className="absolute inset-0"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{ background: isLight ? "rgba(15,15,20,0.35)" : "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)" }}
      />

      {/* Panel */}
      <motion.aside
        className="relative w-full max-w-3xl h-full flex flex-col shadow-2xl"
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", stiffness: 320, damping: 34 }}
        style={{ background: t.bg, borderLeft: `1px solid ${t.border}`, color: t.text }}
      >
        <div
          className="flex items-center justify-between px-6 py-5"
          style={{ borderBottom: `1px solid ${t.border}` }}
        >
          <div>
            <div
              className="text-[11px] uppercase tracking-widest"
              style={{ color: t.muted }}
            >
              Customer profile
            </div>
            <div className="text-lg font-semibold tracking-tight mt-1">
              {detail?.user?.name || "…"}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="customer-detail-close"
            className="h-9 w-9 rounded-full flex items-center justify-center btn-anim"
            style={{ border: `1px solid ${t.border}`, background: t.surface }}
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        {loading || !detail ? (
          <div className="p-8 text-sm" style={{ color: t.muted }}>
            Loading customer…
          </div>
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
            <TabsList
              className="mx-6 mt-5 p-1 squircle-sm inline-flex w-fit"
              style={{ background: t.surface, border: `1px solid ${t.border}` }}
            >
              {[
                { id: "overview", label: "Overview" },
                { id: "orders", label: `Orders (${detail.orders.length})` },
                { id: "content", label: "Content" },
                { id: "internal", label: "Internal" },
              ].map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  data-testid={`detail-tab-${tab.id}`}
                  className={`squircle-sm px-4 py-2 text-sm ${
                    isLight
                      ? "data-[state=active]:bg-black data-[state=active]:text-white"
                      : "data-[state=active]:bg-white data-[state=active]:text-black"
                  }`}
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
              <TabsContent value="overview" className="mt-0">
                <OverviewTab
                  user={detail.user}
                  orders={detail.orders}
                  t={t}
                  onSaved={async () => {
                    await load();
                    onChanged?.();
                  }}
                />
              </TabsContent>
              <TabsContent value="orders" className="mt-0">
                <OrdersTab
                  orders={detail.orders}
                  editors={editors}
                  t={t}
                  onChanged={async () => {
                    await load();
                    onChanged?.();
                  }}
                />
              </TabsContent>
              <TabsContent value="content" className="mt-0">
                <ContentTab orders={detail.orders} t={t} />
              </TabsContent>
              <TabsContent value="internal" className="mt-0">
                <InternalTab
                  user={detail.user}
                  orders={detail.orders}
                  t={t}
                  onSaved={async () => {
                    await load();
                    onChanged?.();
                  }}
                />
              </TabsContent>
            </div>
          </Tabs>
        )}
      </motion.aside>
    </motion.div>
  );
}

/* ---- Tab: Overview (profile info) ---- */
function OverviewTab({ user, orders, t, onSaved }) {
  const [name, setName] = useState(user.name || "");
  const [location, setLocation] = useState(user.location || "");
  const [company, setCompany] = useState(user.company || "");
  const [links, setLinks] = useState(user.links || {});
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/customers/${user.user_id}`, {
        name,
        location,
        company,
        links,
      });
      toast.success("Profile saved");
      onSaved?.();
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const setLink = (k, v) => setLinks((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="space-y-8" data-testid="detail-overview">
      {/* Read-only meta */}
      <div className="grid grid-cols-2 gap-3">
        <MetaTile icon={Mail} label="Email" value={user.email} t={t} />
        <MetaTile
          icon={Calendar}
          label="Joined"
          t={t}
          value={new Date(user.created_at).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        />
        <MetaTile label="Role" value={user.role} t={t} />
        <MetaTile label="Total orders" value={orders.length} t={t} />
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-widest mb-3" style={{ color: t.muted }}>
          Profile
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Full name" value={name} onChange={setName} testid="detail-name" t={t} />
          <Field
            label="Company / brand"
            value={company}
            onChange={setCompany}
            testid="detail-company"
            icon={Building2}
            t={t}
          />
          <Field
            label="Location"
            value={location}
            onChange={setLocation}
            testid="detail-location"
            icon={MapPin}
            t={t}
          />
        </div>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-widest mb-3" style={{ color: t.muted }}>
          Client links
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            ["website", "Website"],
            ["youtube", "YouTube"],
            ["instagram", "Instagram"],
            ["tiktok", "TikTok"],
            ["twitter", "X / Twitter"],
          ].map(([k, label]) => (
            <Field
              key={k}
              label={label}
              value={links[k] || ""}
              onChange={(v) => setLink(k, v)}
              testid={`detail-link-${k}`}
              placeholder="https://…"
              icon={LinkIcon}
              t={t}
            />
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          data-testid="detail-save-profile"
          className="btn-accent squircle-sm px-5 h-10 text-sm font-medium btn-anim inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Save size={14} /> {saving ? "Saving…" : "Save profile"}
        </button>
      </div>
    </div>
  );
}

/* ---- Tab: Orders ---- */
function OrdersTab({ orders, editors, t, onChanged }) {
  if (!orders.length) {
    return <div className="text-sm" style={{ color: t.muted }}>No orders yet.</div>;
  }
  return (
    <div className="space-y-4" data-testid="detail-orders">
      {orders.map((o) => (
        <AdminOrderCard key={o.order_id} order={o} editors={editors} t={t} onChanged={onChanged} />
      ))}
    </div>
  );
}

function AdminOrderCard({ order, editors, t, onChanged }) {
  const total = Number(order.price?.total || 0);
  const paid = Number(order.amount_paid || 0);
  const remaining = Math.max(0, total - paid);
  const [status, setStatus] = useState(order.admin_status || "Pending");
  const [stage, setStage] = useState(order.stage || STAGES[0]);
  const [paidInput, setPaidInput] = useState(paid);
  const [completed, setCompleted] = useState(order.completed_count || 0);
  const [saving, setSaving] = useState(false);
  const [deliverablesOpen, setDeliverablesOpen] = useState(false);

  // Keep local state in sync when the parent re-fetches the order (e.g. after
  // any deliverable/stage change elsewhere pushes fresh data through props).
  React.useEffect(() => {
    setStatus(order.admin_status || "Pending");
    setStage(order.stage || STAGES[0]);
    setPaidInput(Number(order.amount_paid || 0));
    setCompleted(Number(order.completed_count || 0));
  }, [order.order_id, order.admin_status, order.stage, order.amount_paid, order.completed_count]);

  const isDelivered =
    (order.admin_status === "Delivered" || order.stage === "Delivered");

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/orders/${order.order_id}`, {
        admin_status: status,
        stage,
        amount_paid: Number(paidInput || 0),
        completed_count: Number(completed || 0),
      });
      toast.success("Order updated");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update order");
    } finally {
      setSaving(false);
    }
  };

  const isMulti = Array.isArray(order.services) && order.services.length;
  const totalUnits = isMulti
    ? order.services.reduce((s, x) => s + (x.quantity || 0), 0)
    : order.package_clips || 0;

  const stageIdx = Math.max(0, STAGES.indexOf(stage));
  const stagePct = Math.round(((stageIdx + 1) / STAGES.length) * 100);

  return (
    <div
      className="squircle p-5"
      style={{
        border: `1px solid ${isDelivered ? "rgba(34,197,94,0.6)" : t.border}`,
        background: isDelivered
          ? "linear-gradient(180deg, rgba(34,197,94,0.08) 0%, rgba(34,197,94,0.02) 100%)"
          : t.surface2,
        boxShadow: isDelivered
          ? "0 0 0 3px rgba(34,197,94,0.10)"
          : "none",
      }}
      data-testid={`detail-order-${order.order_id}`}
      data-delivered={isDelivered ? "1" : "0"}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs" style={{ color: t.muted }}>
            Order #{order.order_id.slice(-6).toUpperCase()}
          </div>
          <div className="text-base font-semibold tracking-tight mt-1 flex items-center gap-2">
            {isMulti
              ? `${totalUnits} pieces · ${order.services.length} service${order.services.length > 1 ? "s" : ""}`
              : `${totalUnits} · ${order.editing_style || "—"}`}
            {isDelivered && (
              <span
                className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                style={{
                  background: "rgba(34,197,94,0.15)",
                  color: "#22c55e",
                  border: "1px solid rgba(34,197,94,0.4)",
                }}
              >
                Delivered
              </span>
            )}
          </div>
          <div className="text-xs mt-1" style={{ color: t.muted }}>
            Placed {new Date(order.created_at).toLocaleString()} · ETA {new Date(order.estimated_delivery).toLocaleDateString()}
          </div>
        </div>
        <ReceiptButton orderId={order.order_id} />
      </div>

      {/* Stage picker (client-facing timeline) */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <label
            className="text-[10px] uppercase tracking-widest"
            style={{ color: t.muted }}
          >
            Client-facing stage · updates their timeline
          </label>
          <span className="text-[11px]" style={{ color: t.muted }}>
            Step {stageIdx + 1} of {STAGES.length} · {stagePct}%
          </span>
        </div>
        <select
          data-testid={`stage-picker-${order.order_id}`}
          value={stage}
          onChange={(e) => setStage(e.target.value)}
          className="w-full rounded-full px-4 h-10 text-sm"
          style={{
            background: t.input,
            border: `1px solid ${t.border}`,
            color: t.text,
          }}
        >
          {STAGES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <div className="progress-track mt-3">
          <motion.div
            className="progress-fill"
            initial={false}
            animate={{ width: `${stagePct}%` }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
      </div>

      {/* Service breakdown */}
      <div className="mt-5 space-y-2">
        {isMulti ? (
          order.services.map((s, i) => <ServiceLine key={i} service={s} t={t} />)
        ) : (
          <div className="text-xs" style={{ color: t.muted }}>
            Style: {order.editing_style} · Source: {(order.source_links || []).length} link(s)
          </div>
        )}
      </div>

      {/* Per-deliverable editor */}
      <DeliverablesEditor
        order={order}
        t={t}
        open={deliverablesOpen}
        onToggle={() => setDeliverablesOpen((v) => !v)}
        onChanged={onChanged}
      />

      {/* Controls row */}
      <div className="mt-5 grid sm:grid-cols-4 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest" style={{ color: t.muted }}>
            Admin status
          </label>
          <select
            data-testid={`admin-status-${order.order_id}`}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="mt-1 w-full rounded-full px-3 h-9 text-xs"
            style={{ background: t.input, border: `1px solid ${t.border}`, color: t.text }}
          >
            {ADMIN_STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest" style={{ color: t.muted }}>
            Amount paid
          </label>
          <input
            data-testid={`amount-paid-${order.order_id}`}
            type="number"
            step="0.01"
            min={0}
            value={paidInput}
            onChange={(e) => setPaidInput(e.target.value)}
            className="mt-1 w-full rounded-full px-3 h-9 text-xs"
            style={{ background: t.input, border: `1px solid ${t.border}`, color: t.text }}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest" style={{ color: t.muted }}>
            Completed
          </label>
          <input
            data-testid={`completed-${order.order_id}`}
            type="number"
            min={0}
            value={completed}
            onChange={(e) => setCompleted(e.target.value)}
            className="mt-1 w-full rounded-full px-3 h-9 text-xs"
            style={{ background: t.input, border: `1px solid ${t.border}`, color: t.text }}
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={save}
            disabled={saving}
            data-testid={`save-order-${order.order_id}`}
            className="btn-accent squircle-sm px-4 h-9 text-xs font-medium btn-anim inline-flex items-center gap-1.5 disabled:opacity-50 w-full justify-center"
          >
            <Save size={12} /> {saving ? "…" : "Save"}
          </button>
        </div>
      </div>

      <div
        className="mt-4 flex flex-wrap gap-4 text-xs"
        style={{ color: t.muted }}
      >
        <span>Total <span style={{ color: t.text }} className="font-medium">${total.toFixed(2)}</span></span>
        <span>Paid <span style={{ color: t.text }} className="font-medium">${paid.toFixed(2)}</span></span>
        <span>Remaining <span className="font-medium" style={{ color: remaining > 0 ? "#6589ff" : "#22c55e" }}>${remaining.toFixed(2)}</span></span>
      </div>
    </div>
  );
}

/* ---- Per-deliverable editor (Admin → Customer Detail → Orders → each card) ---- */
function DeliverablesEditor({ order, t, open, onToggle, onChanged }) {
  const items = Array.isArray(order.project_deliverables) ? order.project_deliverables : [];
  const total = items.length;
  const done = items.filter((d) => d.stage === DELIVERABLE_STAGES[DELIVERABLE_STAGES.length - 1]).length;

  return (
    <div
      className="mt-5 rounded-2xl overflow-hidden"
      style={{ border: `1px solid ${t.border}`, background: t.surface }}
      data-testid={`deliverables-editor-${order.order_id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 h-11 text-left btn-anim"
        data-testid={`deliverables-editor-toggle-${order.order_id}`}
        style={{ color: t.text }}
      >
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-widest" style={{ color: t.muted }}>
            Deliverables
          </span>
          <span
            className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
            style={{
              background: t.input,
              border: `1px solid ${t.border}`,
              color: t.text,
            }}
          >
            {done}/{total} delivered
          </span>
        </div>
        <ChevronDown
          size={14}
          style={{
            color: t.muted,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 space-y-2" style={{ borderTop: `1px solid ${t.border}` }}>
              {items.length === 0 && (
                <div className="text-xs py-3" style={{ color: t.muted }}>
                  No deliverables scheduled for this order yet.
                </div>
              )}
              {items.map((d) => (
                <DeliverableEditorRow
                  key={d.id}
                  orderId={order.order_id}
                  deliverable={d}
                  t={t}
                  onChanged={onChanged}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function DeliverableEditorRow({ orderId, deliverable, t, onChanged }) {
  const [title, setTitle] = useState(deliverable.title || "");
  const [stage, setStage] = useState(deliverable.stage || DELIVERABLE_STAGES[0]);
  const [url, setUrl] = useState(deliverable.url || "");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    setTitle(deliverable.title || "");
    setStage(deliverable.stage || DELIVERABLE_STAGES[0]);
    setUrl(deliverable.url || "");
  }, [deliverable.id, deliverable.title, deliverable.stage, deliverable.url]);

  const isDelivered = stage === DELIVERABLE_STAGES[DELIVERABLE_STAGES.length - 1];
  const dirty =
    (title || "") !== (deliverable.title || "") ||
    stage !== (deliverable.stage || DELIVERABLE_STAGES[0]) ||
    (url || "") !== (deliverable.url || "");

  const save = async (overrides = {}) => {
    setSaving(true);
    try {
      const payload = {
        title,
        stage,
        url: url || null,
        ...overrides,
      };
      await api.patch(`/orders/${orderId}/deliverables/${deliverable.id}`, payload);
      toast.success("Deliverable saved");
      onChanged?.();
    } catch {
      toast.error("Failed to save deliverable");
    } finally {
      setSaving(false);
    }
  };

  const badgeStyle =
    stage === "Delivered"
      ? { background: "rgba(34,197,94,0.12)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.35)" }
      : stage === "Quality Check"
        ? { background: "rgba(234,179,8,0.12)", color: "#eab308", border: "1px solid rgba(234,179,8,0.35)" }
        : stage === "Order Received"
          ? { background: t.input, color: t.muted, border: `1px solid ${t.border}` }
          : { background: "rgba(101,137,255,0.14)", color: "#6589ff", border: "1px solid rgba(101,137,255,0.4)" };

  return (
    <div
      className="rounded-xl p-3"
      style={{ border: `1px solid ${t.border}`, background: t.bg }}
      data-testid={`admin-deliverable-${deliverable.id}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        {/* Title */}
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Deliverable title"
          data-testid={`deliverable-title-input-${deliverable.id}`}
          className="flex-1 min-w-[180px] rounded-full px-3 h-9 text-sm focus:outline-none"
          style={{ background: t.input, border: `1px solid ${t.border}`, color: t.text }}
        />
        {/* Stage badge (visual) + select */}
        <span
          className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full inline-flex items-center gap-1"
          style={badgeStyle}
        >
          {isDelivered ? <Check size={10} strokeWidth={3} /> : stage === "Order Received" ? null : <Loader2 size={10} className="animate-spin" />}
          {stage}
        </span>
        <select
          value={stage}
          onChange={(e) => setStage(e.target.value)}
          data-testid={`deliverable-stage-picker-${deliverable.id}`}
          className="rounded-full px-3 h-9 text-xs"
          style={{ background: t.input, border: `1px solid ${t.border}`, color: t.text }}
        >
          {DELIVERABLE_STAGES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="File URL (adds Download for client when set)"
          data-testid={`deliverable-url-input-${deliverable.id}`}
          className="flex-1 min-w-[220px] rounded-full px-3 h-9 text-xs focus:outline-none"
          style={{ background: t.input, border: `1px solid ${t.border}`, color: t.text }}
        />
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs h-9 px-3 rounded-full btn-anim"
            style={{ border: `1px solid ${t.border}`, color: t.muted }}
            data-testid={`deliverable-open-${deliverable.id}`}
          >
            <Download size={12} /> Open
          </a>
        )}
        <button
          type="button"
          onClick={() => save()}
          disabled={saving || !dirty}
          data-testid={`deliverable-save-${deliverable.id}`}
          className="btn-accent squircle-sm px-4 h-9 text-xs font-medium btn-anim inline-flex items-center gap-1.5 disabled:opacity-40"
        >
          <Save size={12} /> {saving ? "…" : "Save"}
        </button>
      </div>
    </div>
  );
}



function ServiceLine({ service, t }) {
  const map = {
    premium_short: "Premium Short-Form",
    volume_short: "Volume Short-Form",
    long_form: "Long-Form YouTube",
    thumbnails: "Thumbnails",
    carousels: "Carousels",
    strategy: "Strategy",
  };
  return (
    <div
      className="rounded-xl px-4 py-3"
      style={{ border: `1px solid ${t.border}`, background: t.surface }}
    >
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">
          {map[service.service_id] || service.service_id} · {service.quantity}
        </div>
        <div className="text-xs" style={{ color: t.muted }}>
          Style: {service.style_id}
          {service.footage_minutes ? ` · ~${service.footage_minutes} min raw` : ""}
        </div>
      </div>
      {(service.source_links || []).length > 0 && (
        <div className="mt-2 space-y-1">
          {service.source_links.map((l, i) => (
            <a
              key={i}
              href={l}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs hover:underline inline-flex items-center gap-1.5"
              style={{ color: "#6589ff" }}
            >
              <ExternalLink size={11} /> {l.length > 60 ? l.slice(0, 60) + "…" : l}
            </a>
          ))}
        </div>
      )}
      {service.source_context && (
        <div className="mt-2 text-xs italic" style={{ color: t.muted }}>
          "{service.source_context}"
        </div>
      )}
      {service.notes && (
        <div className="mt-2 text-xs" style={{ color: t.muted }}>
          Client notes: {service.notes}
        </div>
      )}
    </div>
  );
}

/* ---- Tab: Content ---- */
function ContentTab({ orders, t }) {
  const allContent = useMemo(() => {
    const out = [];
    orders.forEach((o) => {
      const svcList = Array.isArray(o.services) && o.services.length
        ? o.services
        : [{
            service_id: "legacy",
            style_id: o.editing_style,
            quantity: o.package_clips,
            source_links: o.source_links || [],
            source_context: null,
            notes: o.notes,
          }];
      svcList.forEach((s) => {
        (s.source_links || []).forEach((link) =>
          out.push({ orderId: o.order_id, service: s.service_id, link, note: s.notes })
        );
        if (s.source_context) {
          out.push({
            orderId: o.order_id,
            service: s.service_id,
            text: s.source_context,
            note: s.notes,
          });
        }
      });
    });
    return out;
  }, [orders]);

  return (
    <div data-testid="detail-content">
      {allContent.length === 0 ? (
        <div className="text-sm" style={{ color: t.muted }}>No submitted content yet.</div>
      ) : (
        <ul className="space-y-2">
          {allContent.map((c, i) => (
            <li
              key={i}
              className="rounded-xl px-4 py-3 flex items-start justify-between gap-3"
              style={{ border: `1px solid ${t.border}`, background: t.surface }}
            >
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-widest" style={{ color: t.muted }}>
                  #{c.orderId.slice(-6).toUpperCase()} · {c.service}
                </div>
                {c.link ? (
                  <a
                    href={c.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm hover:underline break-all"
                    style={{ color: "#6589ff" }}
                  >
                    {c.link}
                  </a>
                ) : (
                  <div className="text-sm break-words">{c.text}</div>
                )}
              </div>
              {c.link && (
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(c.link);
                    toast.success("Copied");
                  }}
                  className="shrink-0"
                  style={{ color: t.muted }}
                  aria-label="Copy"
                >
                  <Copy size={13} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---- Tab: Internal ---- */
function InternalTab({ user, orders, t, onSaved }) {
  const [notes, setNotes] = useState(user.admin_notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/customers/${user.user_id}`, { notes });
      toast.success("Internal notes saved");
      onSaved?.();
    } catch {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="detail-internal">
      <div>
        <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: t.muted }}>
          Internal notes (private)
        </div>
        <textarea
          data-testid="detail-internal-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={6}
          className="w-full rounded-2xl p-4 text-sm focus:outline-none"
          style={{
            background: t.input,
            border: `1px solid ${t.border}`,
            color: t.text,
          }}
          placeholder="Special preferences, VIP flags, team comms — not visible to the client."
        />
        <div className="mt-2 flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            data-testid="detail-save-internal"
            className="btn-accent squircle-sm px-5 h-10 text-sm font-medium btn-anim inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save internal notes"}
          </button>
        </div>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-widest mb-3" style={{ color: t.muted }}>
          Revision history
        </div>
        <RevisionAll orders={orders} t={t} />
      </div>
    </div>
  );
}

function RevisionAll({ orders, t }) {
  const items = orders.flatMap((o) =>
    (o.revisions || []).map((r) => ({ ...r, orderId: o.order_id })),
  );
  if (!items.length)
    return <div className="text-sm" style={{ color: t.muted }}>No revisions logged.</div>;
  return (
    <ul className="space-y-2">
      {items.map((r, i) => (
        <li
          key={i}
          className="rounded-xl px-4 py-3 text-sm"
          style={{ border: `1px solid ${t.border}`, background: t.surface }}
        >
          <div className="text-[11px] uppercase tracking-widest" style={{ color: t.muted }}>
            #{r.orderId.slice(-6).toUpperCase()} · {new Date(r.at).toLocaleString()}
          </div>
          <div className="mt-1">{r.note}</div>
        </li>
      ))}
    </ul>
  );
}

/* ---- shared UI atoms ---- */
function MetaTile({ icon: Icon, label, value, t }) {
  return (
    <div
      className="rounded-2xl px-4 py-3"
      style={{ border: `1px solid ${t.border}`, background: t.surface }}
    >
      <div
        className="text-[10px] uppercase tracking-widest flex items-center gap-1.5"
        style={{ color: t.muted }}
      >
        {Icon && <Icon size={10} />} {label}
      </div>
      <div className="text-sm mt-1 truncate capitalize" style={{ color: t.text }}>
        {value || "—"}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid, icon: Icon, t }) {
  return (
    <label className="block">
      <span
        className="text-[11px] uppercase tracking-widest flex items-center gap-1.5"
        style={{ color: t.muted }}
      >
        {Icon && <Icon size={11} />} {label}
      </span>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full rounded-full px-4 h-10 text-sm focus:outline-none"
        style={{
          background: t.input,
          border: `1px solid ${t.border}`,
          color: t.text,
        }}
      />
    </label>
  );
}

function ReceiptButton({ orderId, className = "" }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  // The receipt endpoint requires Authorization — provide a helper that opens
  // the PDF in a new tab via fetch → blob URL so we can attach the header.
  const view = async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/receipt.pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const win = window.open(url, "_blank");
      // Revoke after a moment so browser can load it
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      if (!win) toast.error("Popup blocked — allow popups to view receipt.");
    } catch {
      toast.error("Failed to load receipt");
    }
  };
  // eslint-disable-next-line no-unused-vars
  const _ = backend;
  return (
    <button
      onClick={view}
      data-testid={`receipt-${orderId}`}
      className={`squircle-sm border border-[#2b2b2b] px-3 h-8 text-xs inline-flex items-center gap-1.5 text-[#a0a0a0] hover:text-white hover:bg-[#0d0d0d] btn-anim ${className}`}
    >
      <Receipt size={12} /> Receipt
    </button>
  );
}


/* ---- Admin: Bliencer Prime panel (members list + revenue stats) ---- */
function PrimePanel() {
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/admin/prime/stats"), api.get("/admin/prime/members")])
      .then(([s, m]) => { setStats(s.data); setMembers(m.data); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-[#a0a0a0]">Loading…</div>;

  const tile = (label, value, sub) => (
    <div key={label} className="squircle border border-[#2b2b2b] bg-[#111] p-6" data-testid={`prime-tile-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="text-xs text-[#a0a0a0] uppercase tracking-widest">{label}</div>
      <div className="text-3xl font-bold mt-3">{value}</div>
      {sub && <div className="text-xs text-[#7a7a7a] mt-2">{sub}</div>}
    </div>
  );

  return (
    <div data-testid="admin-prime-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {tile("MRR", `$${(stats?.mrr || 0).toLocaleString()}`, "monthly recurring revenue")}
        {tile("ARR", `$${(stats?.arr || 0).toLocaleString()}`, "annualized")}
        {tile("Active members", stats?.total_active || 0, `${stats?.prime_active || 0} Prime · ${stats?.prime_pro_active || 0} Prime Pro`)}
        {tile("Cancelled", stats?.cancelled_all_time || 0, "all-time")}
      </div>

      <div className="mt-4 squircle border border-[#2b2b2b] bg-[#111] p-6">
        <div className="text-xs text-[#a0a0a0] uppercase tracking-widest">Total member savings delivered</div>
        <div className="text-3xl font-bold mt-2" style={{ color: "#a855f7" }}>
          ${(stats?.total_member_savings_delivered || 0).toLocaleString()}
        </div>
        <div className="text-xs text-[#7a7a7a] mt-2">Value passed through to your members in the form of discounts.</div>
      </div>

      <div className="mt-8">
        <div className="text-xs uppercase tracking-widest text-[#a0a0a0] mb-3">
          Active members ({members.length})
        </div>
        <div className="squircle border border-[#2b2b2b] overflow-hidden">
          <div className="grid grid-cols-12 bg-[#0d0d0d] text-xs uppercase tracking-widest text-[#a0a0a0] px-4 py-3">
            <div className="col-span-4">Name</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-2">Plan</div>
            <div className="col-span-1">Discount</div>
            <div className="col-span-2 text-right">Lifetime saved</div>
          </div>
          {members.length === 0 && (
            <div className="p-8 text-[#a0a0a0] text-sm text-center">No active members yet.</div>
          )}
          {members.map((u) => {
            const m = u.membership || {};
            return (
              <div key={u.user_id} className="grid grid-cols-12 px-4 py-4 border-t border-[#1e1e1e] items-center bg-[#111]" data-testid={`prime-member-${u.user_id}`}>
                <div className="col-span-4 font-medium">{u.name}</div>
                <div className="col-span-3 text-sm text-[#a0a0a0] truncate">{u.email}</div>
                <div className="col-span-2 text-sm">
                  {m.plan === "prime_pro" ? "Pro Plus" : "Pro"}
                </div>
                <div className="col-span-1 text-sm" style={{ color: "#a855f7" }}>
                  {m.discount_percentage || 0}%
                </div>
                <div className="col-span-2 text-sm text-right font-semibold">
                  ${Number(m.lifetime_savings || 0).toFixed(2)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ---- Admin: Affiliate payouts panel ---- */
function AffiliatesPanel() {
  const [affs, setAffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingCode, setPayingCode] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/affiliates");
      setAffs(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markPaid = async (aff) => {
    const pending = Number(aff.pending_payout || 0);
    if (pending <= 0) return;
    if (!window.confirm(
      `Mark $${pending.toFixed(2)} as paid to ${aff.user_email || aff.referral_code}?`
    )) return;
    setPayingCode(aff.referral_code);
    try {
      const r = await api.post(`/admin/affiliates/${aff.referral_code}/mark-paid`);
      toast.success(`Paid $${r.data.payout.amount.toFixed(2)} to ${aff.user_email}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark paid");
    } finally {
      setPayingCode(null);
    }
  };

  if (loading) return <div className="text-[#a0a0a0]">Loading…</div>;

  const totalPending = affs.reduce((s, a) => s + Number(a.pending_payout || 0), 0);
  const totalEarned = affs.reduce((s, a) => s + Number(a.commission_earned || 0), 0);
  const totalSales = affs.reduce((s, a) => s + Number(a.sales || 0), 0);

  return (
    <div data-testid="admin-affiliates-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          ["Affiliates", affs.length],
          ["Referred sales", totalSales],
          ["Lifetime earned", `$${totalEarned.toFixed(2)}`],
          ["Pending payouts", `$${totalPending.toFixed(2)}`],
        ].map(([l, v]) => (
          <div key={l} className="squircle border border-[#2b2b2b] bg-[#111] p-6">
            <div className="text-xs text-[#a0a0a0] uppercase tracking-widest">{l}</div>
            <div className="text-3xl font-bold mt-3">{v}</div>
          </div>
        ))}
      </div>

      <div className="mt-8">
        <div className="text-xs uppercase tracking-widest text-[#a0a0a0] mb-3">
          All affiliates ({affs.length})
        </div>
        <div className="squircle border border-[#2b2b2b] overflow-hidden">
          <div className="grid grid-cols-12 bg-[#0d0d0d] text-xs uppercase tracking-widest text-[#a0a0a0] px-4 py-3">
            <div className="col-span-3">Affiliate</div>
            <div className="col-span-2">Code</div>
            <div className="col-span-1 text-right">Sales</div>
            <div className="col-span-2 text-right">Earned</div>
            <div className="col-span-2 text-right">Pending</div>
            <div className="col-span-2 text-right">Action</div>
          </div>
          {affs.length === 0 && (
            <div className="p-8 text-[#a0a0a0] text-sm text-center">No affiliates enrolled yet.</div>
          )}
          {affs.map((a) => {
            const pending = Number(a.pending_payout || 0);
            const busy = payingCode === a.referral_code;
            return (
              <div
                key={a.referral_code}
                className="grid grid-cols-12 px-4 py-4 border-t border-[#1e1e1e] items-center bg-[#111]"
                data-testid={`affiliate-row-${a.referral_code}`}
              >
                <div className="col-span-3 min-w-0">
                  <div className="font-medium truncate">{a.user_name || "—"}</div>
                  <div className="text-xs text-[#a0a0a0] truncate">{a.user_email || "—"}</div>
                </div>
                <div className="col-span-2">
                  <code className="text-[11px] text-[#8ab0ff]">{a.referral_code}</code>
                </div>
                <div className="col-span-1 text-sm text-right">{a.sales || 0}</div>
                <div className="col-span-2 text-sm text-right font-semibold">
                  ${Number(a.commission_earned || 0).toFixed(2)}
                </div>
                <div className="col-span-2 text-sm text-right font-semibold" style={{ color: pending > 0 ? "#f59e0b" : "#6b7280" }}>
                  ${pending.toFixed(2)}
                </div>
                <div className="col-span-2 text-right">
                  <button
                    data-testid={`affiliate-mark-paid-${a.referral_code}`}
                    disabled={pending <= 0 || busy}
                    onClick={() => markPaid(a)}
                    className="squircle-sm bg-white text-black px-3 py-1.5 text-xs font-medium btn-anim inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                    {busy ? "Marking…" : "Mark paid"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


function AnalyticsPanel({ data }) {
  if (!data) return <div className="text-[#a0a0a0]">Loading…</div>;
  return (
    <div data-testid="admin-analytics-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          ["Revenue", `$${data.revenue.toLocaleString()}`],
          ["Orders", data.total_orders],
          ["Delivered", data.delivered],
          ["Customers", data.customers],
        ].map(([l, v]) => (
          <div key={l} className="squircle border border-[#2b2b2b] bg-[#111] p-6">
            <div className="text-xs text-[#a0a0a0] uppercase tracking-widest">{l}</div>
            <div className="text-3xl font-bold mt-3">{v}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <div className="squircle border border-[#2b2b2b] bg-[#111] p-8">
          <h3 className="text-lg font-semibold mb-6">Popular styles</h3>
          <ul className="space-y-3">
            {data.popular_styles.length === 0 && <li className="text-[#a0a0a0] text-sm">No data</li>}
            {data.popular_styles.map((s) => (
              <li key={s.style} className="flex items-center justify-between text-sm">
                <span className="capitalize">{s.style.replace(/_/g, " ")}</span>
                <span className="text-[#a0a0a0]">{s.count}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="squircle border border-[#2b2b2b] bg-[#111] p-8">
          <h3 className="text-lg font-semibold mb-6">Popular packages</h3>
          <ul className="space-y-3">
            {data.popular_packages.length === 0 && <li className="text-[#a0a0a0] text-sm">No data</li>}
            {data.popular_packages.map((p) => (
              <li key={p.clips} className="flex items-center justify-between text-sm">
                <span>{p.clips} clips</span>
                <span className="text-[#a0a0a0]">{p.count}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-6 squircle border border-[#2b2b2b] bg-[#111] p-8">
        <h3 className="text-lg font-semibold mb-6">Affiliate totals</h3>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-[#a0a0a0]">Clicks</div>
            <div className="text-2xl font-bold">{data.affiliate_totals.clicks || 0}</div>
          </div>
          <div>
            <div className="text-[#a0a0a0]">Sales</div>
            <div className="text-2xl font-bold">{data.affiliate_totals.sales || 0}</div>
          </div>
          <div>
            <div className="text-[#a0a0a0]">Commission</div>
            <div className="text-2xl font-bold">
              ${Number(data.affiliate_totals.commission || 0).toFixed(2)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
